import { Platform, listFeaturedPlatforms } from "shared/platforms";
import { ActivityKind } from "shared/types/activity";
import Switch from "client/components/common/Switch";
import SelectBox from "client/components/common/SelectBox";
import Tag from "client/components/icons/tag";
import { ShoppingBag } from "client/components/icons/shopping-bag";
import FilterDropdown from "client/components/common/FilterDropdown";
import { useState } from "react";
import SimpleButton from "client/components/common/Button";
import MarketIcon from "client/components/icons/market-icon";

export const ActivityFilter = ({
  kind,
  setKind,
  onlySansaListings,
  toggleOnlySansaListings,
  onlyRelistingListings,
  toggleOnlyRelistingListings,
  onlyNewFloorListings,
  toggleOnlyNewFloorListings,
  onlyNotableAddresses,
  toggleOnlyNotableAddresses,
  platform,
  setPlatform,
  priceMin,
  setPriceMin,
  priceMax,
  setPriceMax,
}: {
  kind?: ActivityKind;
  setKind?: (k: ActivityKind) => void;
  onlySansaListings: boolean;
  toggleOnlySansaListings: () => void;
  onlyRelistingListings: boolean;
  toggleOnlyRelistingListings: () => void;
  onlyNewFloorListings: boolean;
  toggleOnlyNewFloorListings: () => void;
  onlyNotableAddresses: boolean;
  toggleOnlyNotableAddresses: () => void;
  priceMin?: number;
  setPriceMin?: (n?: number) => void;
  priceMax?: number;
  setPriceMax?: (n?: number) => void;
  platform?: Platform;
  setPlatform?: (p?: Platform) => void;
}) => {
  const [platformDropdownOpen, setPlatformDropdownOpen] =
    useState<boolean>(false);
  const [eventLabelsDropdownOpen, setEventLabelsDropdownOpen] =
    useState<boolean>(false);
  const [walletLabelsDropdownOpen, setWalletLabelsDropdownOpen] =
    useState<boolean>(false);

  const [priceDropdownOpen, setPriceDropdownOpen] = useState<boolean>(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState<boolean>(true);

  const [priceMinInput, setPriceMinInput] = useState<number>(0);
  const [priceMaxInput, setPriceMaxInput] = useState<number>(0);

  const platforms = listFeaturedPlatforms();

  const handleKindChange = (kindUpdate: ActivityKind) => {
    // if the kind is sales make sure any listing specific filters are cleared
    if (kindUpdate === "sale") {
      if (onlySansaListings) toggleOnlySansaListings?.();
      if (onlyRelistingListings) toggleOnlyRelistingListings?.();
      if (onlyNewFloorListings) toggleOnlyNewFloorListings?.();
    }
    setKind?.(kindUpdate);
  };

  const handlePriceMinChange = (priceMinUpdate: number) => {
    const val = Number(priceMinUpdate);
    if (!priceMinUpdate || isNaN(val)) {
      setPriceMinInput?.(0);
    } else {
      setPriceMinInput?.(val);
    }
  };

  const handlePriceMaxChange = (priceMinUpdate: number) => {
    const val = Number(priceMinUpdate);
    if (!priceMinUpdate || isNaN(val)) {
      setPriceMaxInput?.(0);
    } else {
      setPriceMaxInput?.(val);
    }
  };

  const handleTogglePlatform = (platformUpdate: Platform) => {
    //  if the platform is already set we are unchecking it
    if (platformUpdate === platform) {
      setPlatform?.(undefined);
    } else {
      setPlatform?.(platformUpdate);
    }
  };

  return (
    <div className="flex flex-col gap-3 mt-8">
      <FilterDropdown
        title={"Event Type"}
        open={eventDropdownOpen}
        toggleOpen={() => setEventDropdownOpen((prev: boolean) => !prev)}
      >
        <div className="flex flex-col gap-2">
          <SelectBox
            title="Listings"
            icon={<Tag size={20} />}
            onChange={() => handleKindChange("ask")}
            isSelected={kind === "ask"}
            bordered={true}
          />
          <SelectBox
            title="Sales"
            icon={<ShoppingBag size={20} />}
            onChange={() => handleKindChange("sale")}
            isSelected={kind === "sale"}
            bordered={true}
          />
        </div>
      </FilterDropdown>

      <FilterDropdown
        title={"Price"}
        open={priceDropdownOpen}
        toggleOpen={() => setPriceDropdownOpen((prev: boolean) => !prev)}
      >
        <div className="flex items-center gap-2">
          <input
            className="text-black border-2 dark:border-neutral-700 rounded-xl dark:text-white dark:placeholder-neutral-500 dark:bg-neutral-900 h-12 px-2 w-full overflow-scroll flex-grow"
            placeholder="Min"
            value={priceMinInput}
            type="number"
            onChange={(e: any) => handlePriceMinChange(e.target.value)}
          />
          <input
            className="text-black border-2 dark:border-neutral-700 rounded-xl dark:text-white dark:placeholder-neutral-500 dark:bg-neutral-900 h-12 px-2 w-full overflow-scroll flex-grow"
            placeholder="Max"
            value={priceMaxInput}
            type="number"
            onChange={(e: any) => handlePriceMaxChange(e.target.value)}
          />
        </div>
        <div className="mt-2">
          <SimpleButton
            className="w-full py-2 justify-center"
            onClick={() => {
              setPriceMin?.(priceMinInput);
              setPriceMax?.(priceMaxInput);
            }}
          >
            Apply
          </SimpleButton>
        </div>
      </FilterDropdown>
      <FilterDropdown
        title={"Platform"}
        open={platformDropdownOpen}
        toggleOpen={() => setPlatformDropdownOpen((prev: boolean) => !prev)}
      >
        <div className="flex flex-col gap-1">
          {platforms.map((plat) => (
            <SelectBox
              key={plat.kind}
              title={plat.name}
              icon={<img src={plat.logoImageUrl} className="h-5 w-5" />}
              onChange={() => handleTogglePlatform(plat.kind)}
              isSelected={platform === plat.kind}
            />
          ))}
        </div>
      </FilterDropdown>

      {/* listing only labels */}
      {kind === "ask" ? (
        <>
          <FilterDropdown
            title={"Event Labels"}
            open={eventLabelsDropdownOpen}
            toggleOpen={() =>
              setEventLabelsDropdownOpen((prev: boolean) => !prev)
            }
          >
            <div className="flex flex-col gap-1">
              <SelectBox
                title="Relistings"
                onChange={() => toggleOnlyRelistingListings?.()}
                isSelected={onlyRelistingListings}
              />
              <SelectBox
                title="New floors"
                onChange={() => toggleOnlyNewFloorListings?.()}
                isSelected={onlyNewFloorListings}
              />
            </div>
          </FilterDropdown>

          <div className="px-2 mt-2">
            <hr className="dark:border-neutral-700" />
            <div className="flex items-center justify-between py-2 mt-4">
              <div className="flex items-center gap-1">
                <MarketIcon name="Sansa" />
                <p>Only Art Blocks</p>
              </div>
              <Switch
                enabled={onlySansaListings}
                setEnabled={() => toggleOnlySansaListings?.()}
                size="sm"
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ActivityFilter;
